import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Playground } from '../../components/Playground';
import { Callout } from '../../components/Callout';

export function TemplatesPage(): Node {
  return html`
    <div>
      <h1>Templates (html)</h1>
      <p>The <code>html</code> tagged template literal creates real DOM nodes — no virtual DOM, no diffing. Reactive expressions (functions) are tracked and updated in place.</p>

      <h2>Basic Usage</h2>
      ${CodeBlock(`import { html } from 'onefold';

// Static content
html\`<div class="card">Hello World</div>\`

// Reactive text
html\`<span>\${() => count()}</span>\`

// Reactive attributes
html\`<div class=\${() => active() ? 'active' : ''}> ... </div>\`

// Static attributes
html\`<div class=\${cls}> ... </div>\``)}

      <h2>Styles</h2>
      ${CodeBlock(`// Style as string
html\`<div style="color: red; font-size: 16px;">...</div>\`

// Style as object (reactive-friendly)
html\`<div style=\${{ color: 'red', fontSize: '16px' }}>...</div>\``)}

      <h2>Events</h2>
      ${CodeBlock(`// Inline handler
html\`<button onclick=\${() => count.set(n => n + 1)}>Click</button>\`

// Named handler
const handleClick = (e: Event) => { /* ... */ };
html\`<button onclick=\${handleClick}>Click</button>\``)}

      <h2>Reactive Lists</h2>
      ${CodeBlock(`const items = createSignal(['Apple', 'Banana', 'Cherry']);

html\`<ul>
  \${() => items().map(item => html\`<li>\${item}</li>\`)}
</ul>\``)}

      ${Callout('The key pattern: wrap dynamic values in () => to make them reactive. Without the arrow, the value is captured once and never updates.')}

      <h2>Two-Way Input Binding</h2>
      <p>Bind a signal to an input's value so the DOM stays in sync when the signal resets:</p>
      ${CodeBlock(`const name = createSignal('');

html\`<input
  value=\${() => name()}
  oninput=\${(e: Event) => name.set((e.target as HTMLInputElement).value)}
/>\`

// Clearing the signal visually clears the input:
name.set('');`)}

      <h2>Refs</h2>
      <p>Access the underlying DOM element after it's created:</p>
      ${CodeBlock(`html\`<input ref=\${(el) => el.focus()} />\``)}

      <h2>Directives</h2>
      <p>Use registered directives with the <code>d-</code> prefix:</p>
      ${CodeBlock(`// Register once
registerDirective('tooltip', (el, value) => { /* ... */ });

// Use in templates
html\`<button d-tooltip="Save changes">Save</button>\``)}

      <h2>Conditional Rendering</h2>
      ${CodeBlock(`html\`<div>
  \${() => loggedIn()
    ? html\`<span>Welcome, \${() => user().name}</span>\`
    : html\`<a href="/login">Sign in</a>\`
  }
</div>\``)}

      <h2>Try It</h2>
      ${Playground(`import { createSignal, html, mount } from 'onefold';

function App(): Node {
  const items = createSignal(['Apple', 'Banana', 'Cherry']);
  const newItem = createSignal('');

  const addItem = () => {
    if (newItem().trim()) {
      items.set(prev => [...prev, newItem()]);
      newItem.set('');
    }
  };

  return html\`
    <div>
      <h3>Shopping List</h3>
      <ul>\${() => items().map(item => html\`<li>\${item}</li>\`)}</ul>
      <input
        placeholder="Add item..."
        oninput=\${(e) => newItem.set(e.target.value)}
        value=\${() => newItem()}
      />
      <button onclick=\${addItem}>Add</button>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'Reactive List')}
    </div>
  `;
}
